// * React Hooks
import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";

// * Routes
import * as URL from "./Routes";
import ProtectedRoute from "src/helpers/ProtectedRoute";
import BrokenRecords from "src/components/BrokenRecords/BrokenRecords";
import BrokenRecordsListing from "src/components/BrokenRecords/BrokenRecordsListing";
import Reports from "src/views/pages/Subpages/ReportPage"

// * Pages
const Login = lazy(() => import("../views/pages/Login/Login"));
const Register = lazy(() => import("../views/pages/register/Register"));
const Page404 = lazy(() => import("../views/pages/page404/Page404"));
const Page505 = lazy(() => import("../views/pages/page500/Page500"));
const Dashboard = lazy(() => import("../views/dashboard/Dashboard"));

const NewsList = lazy(() => import("../views/pages/News/List"));
const NewsView = lazy(() => import("../views/pages/News/NewsView/NewsView"));
const AddNews = lazy(() => import("../views/pages/News/AddNews/AddNews"));
const EditNews = lazy(() => import("../views/pages/News/EditNews/EditNews"));

const ImagesList = lazy(() => import("../views/pages/Images/List"));
const AddImages = lazy(() => import("../views/pages/Images/AddMedia/AddMedia"));
const MediaView = lazy(() =>
  import("../views/pages/Images/MediaView/MediaView")
);
const EditMedia = lazy(() =>
  import("../views/pages/Images/EditMedia/EditMedia")
);

const VideoList = lazy(() => import("../views/pages/Videos/List"));
const AddVideo = lazy(() => import("../views/pages/Videos/AddVideo/AddVideo"));
const VideoView = lazy(() =>
  import("../views/pages/Videos/VideoView/VideoView")
);
const EditVideo = lazy(() =>
  import("../views/pages/Videos/EditVideo/EditVideo")
);

const CategoryList = lazy(() => import("../views/pages/Category/List"));
const AddCategory = lazy(() =>
  import("../views/pages/Category/AddNewsCategory/AddNewsCategory")
);

const EditCategory = lazy(() =>
  import("../views/pages/Category/EditNewsCategory/EditNewsCategory")
);

const VersionList = lazy(() => import("../views/pages/AppVersions/List"));
const AddVersion = lazy(() =>
  import("../views/pages/AppVersions/AddVersion/AddVersion")
);

const EditVersion = lazy(() =>
  import("../views/pages/AppVersions/EditVersion/EditVersion")
);

const NotificationList = lazy(() => import("../views/pages/Notifications/List"));
const AddNotification = lazy(() => import("../views/pages/Notifications/AddNotifications/AddNotifications"));
const EditNotification = lazy(() => import("../views/pages/Notifications/EditNotifications/EditNotifications"));
const ViewNotification = lazy(() => import("../views/pages/Notifications/ViewNotifications/ViewNotifications"));

const UpdateStripList = lazy(() => import("../views/pages/UpdateStrip/List"));
const AddUpdateStrip = lazy(() => import("../views/pages/UpdateStrip/AddUpdateStrip"));
const EditUpdateStrip = lazy(() => import("../views/pages/UpdateStrip/EditUpdateStrip"));

const HeroBannerList = lazy(() => import("../views/pages/HeroBanner/List"));
const AddHeroBanner = lazy(() => import("../views/pages/HeroBanner/AddHeroBanner"));
const EditHeroBanner = lazy(() => import("../views/pages/HeroBanner/EditHeroBanner"));

const EventSpotlightList = lazy(() => import("../views/pages/EventSpotlight/List"));
const AddEventSpotlight = lazy(() => import("../views/pages/EventSpotlight/AddEventSpotlight"));
const EditEventSpotlight = lazy(() => import("../views/pages/EventSpotlight/EditEventSpotlight"));


// * Page Layout Component
const Layout = lazy(() => import("../layout/Layout"));

// * Loader
const Loader = () => (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

export default function Router() {
  const Navigate = useNavigate();
  useEffect(() => {
    if (window.location.pathname === "/") Navigate("/login");
  });
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={URL.Login}
          element={
            <ProtectedRoute accessBy="non-authenticated">
              <Login />
            </ProtectedRoute>
          }
        />
        <Route
          path={URL.Dashboard}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<Dashboard />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.NewsList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<NewsList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddNews}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddNews />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditNewsId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditNews />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.ViewNews}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<NewsView />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.ImagesList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<ImagesList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddImages}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddImages />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.VideoList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<VideoList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddVideo}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddVideo />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.ViewImages}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<MediaView />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditImagesId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditMedia />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.ViewVideo}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<VideoView />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditVideoId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditVideo />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.NewCategoryList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<CategoryList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddNewsCategory}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddCategory />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditCategoryId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditCategory />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AppVersionList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<VersionList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.NotificationList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<NotificationList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddNotification}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddNotification />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditNotificationId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditNotification />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.ViewNotificationId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<ViewNotification />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddVersion}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddVersion />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditVersionId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditVersion />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.BrokenRecords}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<BrokenRecords />} />
            </ProtectedRoute>
          }
        />
        <Route
          path={URL.BrokenRecordsList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<BrokenRecordsListing />} />
            </ProtectedRoute>
          }
        />
        <Route
          path={URL.Reports}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<Reports />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.UpdateStripList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<UpdateStripList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddUpdateStrip}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddUpdateStrip />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditUpdateStripId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditUpdateStrip />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.HeroBannerList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<HeroBannerList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddHeroBanner}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddHeroBanner />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditHeroBannerId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditHeroBanner />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EventSpotlightList}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EventSpotlightList />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.AddEventSpotlight}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<AddEventSpotlight />} />
            </ProtectedRoute>
          }
        />

        <Route
          path={URL.EditEventSpotlightId}
          element={
            <ProtectedRoute accessBy="authenticated">
              <Layout component={<EditEventSpotlight />} />
            </ProtectedRoute>
          }
        />

        <Route path={URL.Register} element={<Register />} />
        <Route path={URL.Page500} element={<Page505 />} />
        <Route path="*" element={<Page404 />} />
      </Routes>
    </Suspense>
  );
}
